// TODO: [International DB] the strict errors here seem more like symptopms of existing patterns across all inputs
// all are type assignments such as: 'string | undefined' is not assignable to parameter of type 'string'.
// feels out of scope to fix now, but will revisit
import { groupBy } from 'lodash/fp';
import { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';

import ThemedFormInternationalDb, {
  CountryOption,
  IdTypeOption,
} from 'inquiry/components/InputInternationalDb/ThemedFormInternationalDb';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { useShouldShowServerError } from 'inquiry/lib/serverError';
import { InputInternationalDbComponent, InternationalId } from 'lib/interfaces/ui-components';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';
import { RootState } from 'verify/redux/store';

interface InputInternationalDbProps {
  component: InputInternationalDbComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

const InputInternationalDb: FC<InputInternationalDbProps> = ({
  component,
  inquiry,
  actionsApi,
  inquiryLoading,
}: InputInternationalDbProps) => {
  const fieldKeyIdbCountry = `${component.name}.idb_country`;
  const fieldKeyIdbType = `${component.name}.idb_type`;
  const fieldKeyIdbValue = `${component.name}.idb_value`;

  const {
    'allowed-id-types': allowedIdTypes,
    'hide-country-if-prefilled': hideCountryIfPrefilled,
    'hide-type-if-prefilled': hideTypeIfPrefilled,
    'hide-country-if-single-choice': hideCountryIfSingleChoice,
    'hide-type-if-single-choice': hideTypeIfSingleChoice,
    // Prefills
    'prefill-idb-country': prefillIdbCountry,
    'prefill-idb-type': prefillIdbType,
    'prefill-idb-value': prefillIdbValue,
    // Validations
    'validations-idb-country': validationsIdbCountry,
    'validations-idb-type': validationsIdbType,
    'validations-idb-value': validationsIdbValue,
    // Translations
    label,
    'label-idb-country': labelIdbCountry,
    'label-idb-type': labelIdbType,
    'label-idb-value': labelIdbValue,
    'placeholder-idb-country': placeholderIdbCountry,
    'placeholder-idb-type': placeholderIdbType,
    'placeholder-idb-value': placeholderIdbValue,
    // Errors
    'error-idb-country': errorIdbCountry,
    'error-idb-type': errorIdbType,
    'error-idb-value': errorIdbValue,
    // form configs
    disabled,
    hidden,
    optional,
  } = component.attributes;

  const shouldShowIdbCountryError = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyIdbCountry,
    prefillIdbCountry,
  );
  const shouldShowIdbTypeError = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyIdbType,
    prefillIdbType,
  );
  const shouldShowIdbValueError = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    fieldKeyIdbValue,
    prefillIdbValue,
  );

  // if any input is touched, clear all server errors
  const shouldShowServerError =
    shouldShowIdbCountryError && shouldShowIdbTypeError && shouldShowIdbValueError;

  const apply = useApply('', inquiry.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  const validateIdbCountry = useValidators({ optional, validations: validationsIdbCountry });
  const validateIdbType = useValidators({ optional, validations: validationsIdbType });
  const validateIdbValue = useValidators({ optional, validations: validationsIdbValue });

  const formName = inquiry.attributes['next-step'].name;
  const selectedCountry = useSelector<RootState, string>((state) =>
    formValueSelector(formName)(state, fieldKeyIdbCountry),
  );
  const selectedType = useSelector<RootState, string>((state) =>
    formValueSelector(formName)(state, fieldKeyIdbType),
  );

  const countryToIdTypesMap: { [key: string]: InternationalId[] } = useMemo(
    () => groupBy('country-code', allowedIdTypes),
    [allowedIdTypes],
  );

  const countryOptions: CountryOption[] = useMemo(() => {
    return Object.keys(countryToIdTypesMap).map((countryCode: string) => {
      return {
        // if the country code exists, it will have at least one entry
        name: countryToIdTypesMap[countryCode][0]['country-name'],
        code: countryCode,
      };
    });
  }, [countryToIdTypesMap]);

  const idTypeOptions: IdTypeOption[] = useMemo(() => {
    if (!selectedCountry) {
      return [];
    }

    const idTypes = countryToIdTypesMap[selectedCountry]?.map((idb: InternationalId) => ({
      idbType: idb['id-type'],
      idbName: idb['name'],
    }));

    if (idTypes == null) {
      return [];
    }

    return idTypes;
  }, [countryToIdTypesMap, selectedCountry]);

  const allowedCountries: Set<string> = useMemo(
    () => new Set(countryOptions.map(({ code }) => code)),
    [countryOptions],
  );
  const allowedIdTypesForPrefilledCountry: Set<string> = useMemo(() => {
    if (!prefillIdbCountry) {
      return new Set();
    }

    return new Set(countryToIdTypesMap[prefillIdbCountry]?.map(({ 'id-type': idType }) => idType));
  }, [prefillIdbCountry, countryToIdTypesMap]);

  const countryPrefilled = !!prefillIdbCountry && allowedCountries.has(prefillIdbCountry);
  const typePrefilled = !!prefillIdbType && allowedIdTypesForPrefilledCountry.has(prefillIdbType);

  useEffect(() => {
    if (isHidden) {
      actionsApi.setField(fieldKeyIdbCountry, '');
      actionsApi.setField(fieldKeyIdbType, '');
      actionsApi.setField(fieldKeyIdbValue, '');
    } else {
      // This cascades because we only want to set nested fields if the parent field is valid
      if (countryPrefilled) {
        actionsApi.setField(fieldKeyIdbCountry, prefillIdbCountry);
        if (typePrefilled) {
          // Only prefill type if country was successfully prefilled
          actionsApi.setField(fieldKeyIdbType, prefillIdbType);
          if (prefillIdbValue) {
            // Only prefill value if both country and type were successfully prefilled
            actionsApi.setField(fieldKeyIdbValue, prefillIdbValue);
          }
        }
      } else {
        // no prefills
        // NOTE: only call this once, because the number of countries available will not change
        if (countryOptions.length === 1) {
          actionsApi.setField(fieldKeyIdbCountry, countryOptions[0].code);
        }
      }
    }
  }, [isHidden]);

  // NOTE: this should check every time idTypeOptions gets updated
  // setField intentionally excluded from dependencies due to FLE loading issue
  useEffect(() => {
    if (isHidden) return;
    if (idTypeOptions.length === 1) {
      actionsApi.setField(fieldKeyIdbType, idTypeOptions[0].idbType);
    }
  }, [isHidden, fieldKeyIdbType, idTypeOptions]);

  if (isHidden) {
    return null;
  }

  return (
    <>
      <ThemedFormInternationalDb
        allowedIdTypes={allowedIdTypes}
        countryOptions={countryOptions}
        idTypeOptions={idTypeOptions}
        hideCountryIfPrefilled={hideCountryIfPrefilled}
        hideTypeIfPrefilled={hideTypeIfPrefilled}
        hideCountryIfSingleChoice={hideCountryIfSingleChoice}
        hideTypeIfSingleChoice={hideTypeIfSingleChoice}
        disabled={apply<boolean>(disabled)}
        // field references
        fieldKeyIdbCountry={fieldKeyIdbCountry}
        fieldKeyIdbType={fieldKeyIdbType}
        fieldKeyIdbValue={fieldKeyIdbValue}
        // Translations
        label={label}
        labelIdbCountry={labelIdbCountry}
        labelIdbType={labelIdbType}
        labelIdbValue={labelIdbValue}
        placeholderIdbCountry={placeholderIdbCountry}
        placeholderIdbType={placeholderIdbType}
        placeholderIdbValue={placeholderIdbValue}
        validateIdbCountry={validateIdbCountry}
        validateIdbType={validateIdbType}
        validateIdbValue={validateIdbValue}
        // server errors
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        serverErrorIdbCountry={shouldShowServerError ? errorIdbCountry : null}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        serverErrorIdbType={shouldShowServerError ? errorIdbType : null}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        serverErrorIdbValue={shouldShowServerError ? errorIdbValue : null}
        // Other
        styles={component.styles}
        setField={actionsApi.setField}
        selectedCountry={selectedCountry}
        selectedType={selectedType}
        countryPrefilled={countryPrefilled}
        typePrefilled={typePrefilled}
      />
    </>
  );
};

export default InputInternationalDb;
